import i18next from 'i18next';

import { Charge } from '@r6digital/rapidstor-types/react/entities/Charge';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import { LineItem } from '@r6digital/rapidstor-types/types/Merchandise';
import { CostRetrieveResponse } from '@r6digital/rapidstor-types/types/Reservation';

import { formatCurrency } from '@/utils/currency';

import { CostRetrieveItem } from '@/components/rapidstor/wizard/pricing-table';

import { CorporationCode, getCorpTranslations } from '../translations/helpers';

/**
 * Helper method to check if a unitType has a deal or not
 * @param unitType
 * @returns boolean
 */
export const unitTypeHasDeal = (unitType: UnitType): boolean => {
    if (unitType.deal) {
        return true;
    } else {
        return false;
    }
};

/**
 * @param descriptor
 *
 * @returns number
 */
export const descriptorDealCount = (descriptor: Descriptor): number => {
    return descriptor.deals.length;
};

/**
 * Timeframe label formatting
 *
 * @param location
 *
 * @returns string
 */
export const formatTimeframe = (location: Location): string => {
    return `${location.rapidstor.showWeeklyRates
        ? i18next.t('time:per_week')
        : location.settings.billingDefault === '28DayAnniversary'
            ? ' per 4 weeks'
            : i18next.t('time:per_month')
        }`;
};

/**
 * Size label formatting
 *
 * @param value
 * @param location
 *
 * @returns string
 */
export const formatSize = (
    value: number,
    location: Location,
    noSubscript?: boolean
): string => {
    if (
        location.rapidstor.areaSizeSymbol === 'm' ||
        location.rapidstor.areaSizeSymbol === 'm²'
    ) {
        return `${value}m${!noSubscript ? '²' : ''}`;
    } else {
        return `${value}${!noSubscript ? 'sq' : ''}ft`;
    }
};

/**
 * Percentage difference for discount display
 *
 * @param original
 * @param discounted
 *
 * @returns string
 */
export const percentageDifference = (original: number, discounted: number): string => {
    const savings = ((original - discounted) / original) * 100;
    return `${savings.toFixed()}%`;
};

/**
 * Rounds a number to a specified number of decimal places
 *
 * @param value
 * @param decimals
 *
 * @returns number
 */
export const numberRound = (value: number, decimals = 2): number => {
    if (!value) {
        return value;
    }
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}

/**
 * Method used for determining the sizing label breakpoints
 *
 * @param size
 * @param location
 *
 * @returns string
 */
export const sizeLabel = (size: number, location: Location): string => {
    if (size <= location.rapidstor.smallMediumSizes) {
        return i18next.t('labels:small');
    } else if (size <= location.rapidstor.mediumLargeSizes) {
        return i18next.t('labels:medium');
    } else {
        return i18next.t('labels:large');
    }
};

/**
 * This control the descriptor labels, respecting rapidstor settings to either use descriptorTitle.
 * Or if not selected use either area or dimensions.
 *
 * @param descriptor
 * @param location
 *
 * @returns string
 */
export const descriptorTitle = (
    descriptor: Descriptor,
    location: Location
): (string | number)[] => {
    if (location.rapidstor.useDescriptorTitle) {
        const corpReplacements = getCorpTranslations(location.corpCode as CorporationCode);

        if (!corpReplacements) {
            return [0, `${descriptor.name}`];
        }

        if (corpReplacements && !('replacements' in corpReplacements)) {
            return [0, `${descriptor.name}`];
        }

        if (corpReplacements.replacements && typeof corpReplacements.replacements === 'object' && descriptor.name in corpReplacements.replacements) {
            return [0, corpReplacements.replacements[descriptor.name as keyof typeof corpReplacements.replacements]];
        }

        return [0, `${descriptor.name}`];
    } else {
        if (location.settings.showAreaInsteadOfDimensions) {
            return [1, `${formatSize(descriptor.area, location)}`];
        } else {
            return [
                2,
                `${formatSize(
                    descriptor.unitTypes[0].length,
                    location,
                    true
                )} x ${formatSize(descriptor.unitTypes[0].width, location, true)}`,
            ];
        }
    }
};

export const descriptorDescriptions = (
    descriptor: Descriptor,
    deal: boolean
): string[] => {
    const descriptions: string[] = [];

    if (deal && descriptor.unitTypes[0].deal?.descriptions) {
        for (const d of descriptor.unitTypes[0].deal.descriptions) {
            descriptions.push(d);
        }
    } else {
        if (descriptor.descriptions) {
            for (const d of descriptor.descriptions) {
                descriptions.push(d);
            }
        }
    }
    return descriptions.splice(0, 4);
};

/**
 * UnitType sorting helper, supports primary, secondary and tertiary for various levels of sorting
 *
 * @param unitTypes
 * @param location
 *
 * @returns UnitType[]
 */
export const sortUnitTypes = (unitTypes: UnitType[], location: Location): UnitType[] => {
    const array = [...unitTypes];
    const sortOrder = ['entryLocation', ...location.settings.unitSortOrder].reverse();
    let sortedArray = array;
    sortOrder.push('none');
    for (const order of sortOrder) {
        sortedArray = array.sort((a, b) => {
            switch (order) {
                case 'size':
                    // Sort by size in ASC order
                    return a.area - b.area;

                case 'floor':
                    // Sort by floor in ASC order
                    return a.floor - b.floor;

                case 'price':
                    // Sort by rate in ASC order
                    return !a.rates.discounted
                        ? a.rates.discountedRate - b.rates.discountedRate
                        : a.rates.rate - b.rates.rate;

                case 'availability':
                    return b.units.length - a.units.length;

                case 'none':
                    return !!a.units.length === !!b.units.length
                        ? 0
                        : a.units.length > 0
                            ? -1
                            : 1;

                default:
                    return 0;
            }
        });
    }

    sortedArray = sortedArray.filter(
        (unitType, index) =>
            sortedArray.findIndex((u) => u.floor === unitType.floor) === index
    );
    return sortedArray;
};

/**
 * Returns the first available UnitType based on the location rapidstor sort settings.
 * If there are none available it will return null.
 *
 * @param unitTypes
 * @param location
 *
 * @returns UnitType | null
 */
export const firstAvailableUnitType = (
    unitTypes: UnitType[],
    location: Location
): UnitType => {
    const sortedUnitTypes = sortUnitTypes(unitTypes, location);
    return sortedUnitTypes[0];
};

/**
 * Handy helper function that returns a boolean for whether a descriptor has available unitTypes
 *
 * @param descriptor
 *
 * @returns boolean
 */
export const isDescriptorAvailable = (descriptor: Descriptor) => {
    if (!descriptor || !descriptor.unitTypes || descriptor.unitTypes.length === 0) {
        return false;
    }

    const unitCount = descriptor.unitTypes.reduce(
        (acc, unitType) => acc + unitType.units.length,
        0
    );

    return unitCount > 0;
};

export const transformCostRetrieve = (
    charges: Charge[] | undefined,
    costRetrieve: CostRetrieveResponse | undefined,
    cart: LineItem[] | undefined,
    merchandiseDiscount: number
): CostRetrieveItem[] => {
    const items: CostRetrieveItem[] = [];

    if (charges) {
        charges.map((charge) => {
            if (charge.chargeCategory === 'ReservFee') {
                items.push({
                    chargeAmount: charge.price,
                    initialChargeAmount: charge.price,
                    initialTaxAmount: numberRound(charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100)),
                    initialTotal: numberRound(charge.price + charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100)),
                    chargeDescription: charge.chargeDescription,
                    discountTaxAmount: 0,
                    discountChargeAmount: 0,
                    taxAmount: numberRound(charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100)),
                    discountAmount: 0,
                    total: numberRound(charge.price + charge.price * ((charge.tax1Rate + charge.tax2Rate) / 100))
                });
            }
        });
    }

    if (costRetrieve?.costRetrieveFormatted) {
        costRetrieve.costRetrieveFormatted.map((charge) => {
            const originalTaxAmount = (charge.dcPriceTax1 || charge.TaxAmount) + (charge.dcPriceTax2 || charge.TaxAmount2);
            // TaxAmount will be the discounted tax amount if the charge has a discount. But it will also be the full tax amount
            // if the charge has no discount. Doing it this way could have some issues.
            const discountTaxAmount = charge.TaxAmount + charge.TaxAmount2;
            const originalChargeAmount = charge.ChargeAmountRnd || charge.ChargeAmount;
            // don't use dcDiscount on the charge, as it includes tax and we need the non-tax discount amount
            // we have to do it this way because, in some cases, the API doesn't return the discount charge 
            // amount, only the discounted tax amount.
            const discountWithoutTax = charge.dcDiscount ? charge.dcDiscount - discountTaxAmount : 0;
            const discountChargeAmount = charge.ChargeAmount !== originalChargeAmount ?
                charge.ChargeAmount : 
                originalChargeAmount - discountWithoutTax;

            items.push({
                chargeAmount: charge.ChargeAmount,
                initialChargeAmount: numberRound(originalChargeAmount),
                initialTaxAmount: numberRound(originalTaxAmount),
                initialTotal: numberRound(originalChargeAmount + originalTaxAmount),
                chargeDescription: charge.ChargeDescription,
                taxAmount: numberRound(charge.TaxAmount + charge.TaxAmount2),
                discountChargeAmount: numberRound(discountChargeAmount),
                discountTaxAmount: numberRound(originalTaxAmount - discountTaxAmount),
                discountAmount: numberRound(((originalChargeAmount - discountChargeAmount) + (originalTaxAmount - discountTaxAmount))),
                discountName: charge.sPlanName,
                total: charge.dcTotal,
            });
        });
    }

    if (cart) {
        cart.map((item) => {
            if (item.quantity > 0) {
                const amount = item.price * item.quantity;
                const tax = item.tax * item.quantity;
                let taxDiscount = 0.0;
                let discountAmount = 0.0;
                let total = 0.0;

                if (merchandiseDiscount > 0) {
                    discountAmount = amount * (merchandiseDiscount / 100);
                    taxDiscount = tax * (merchandiseDiscount / 100);
                }

                total = amount + tax - discountAmount - taxDiscount;

                items.push({
                    chargeAmount: numberRound(item.price * item.quantity),
                    chargeDescription: item.name,
                    initialChargeAmount: numberRound(item.price * item.quantity),
                    initialTaxAmount: numberRound(tax),
                    initialTotal: numberRound(amount + tax),
                    discountTaxAmount: numberRound(taxDiscount),
                    discountChargeAmount: numberRound(item.price * item.quantity - discountAmount),
                    taxAmount: numberRound(item.tax * item.quantity),
                    discountAmount: numberRound(discountAmount + taxDiscount),
                    total: numberRound(total),
                });
            }
        });
    }

    return items;
};

const special = [
    'Ground',
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
];
const endings = ['st', 'nd', 'rd', 'th'];

export const stringifyNumber = (n: number) => {
    if (n < 0) return 'Basement';

    if (n < 11) return special[n];

    if (n >= 11 && n < 20) return `${n}th`;

    const secondNumber = String(n).charAt(1);

    if (secondNumber === '1') return `${n}${endings[0]}`;
    if (secondNumber === '2') return `${n}${endings[1]}`;
    if (secondNumber === '3') return `${n}${endings[2]}`;
    return `${n}${endings[3]}`;
};

export const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

export const formatDealCustomText = (
    deal: Deal,
    location: Location,
    text: string | undefined
) => {
    text = text?.replace(
        /{{DiscountRate}}/g,
        formatCurrency(
            deal.rates.discountedRate ?? deal.rates.rate,
            location,
            false,
            true
        )
    );
    text = text?.replace(
        /{{DiscountSaving}}/g,
        formatCurrency(
            (deal.rates.discountedRate ?? deal.rates.rate) - deal.rates.rate,
            location,
            false,
            true
        ).replace(/-/g, '')
    );
    text = text?.replace(
        /{{StandardRate}}/g,
        formatCurrency(deal.rates.rate, location, false, true)
    );
    return text;
};

interface ErrorResponse {
    reasonCode: string;
    reasonText: string;
    responseCode: string;
}

export const deconstructScaErrorMessage = (errorMessage: string): ErrorResponse | null => {
    const regex = /Reason Code:\s*(\w+)\s*Reason Text:\s*(.*)/;
    const match = errorMessage.match(regex);

    // This is because the error message doesn't have a code to indentify it.
    const alreadyRentedMessage = 'Unit is already rented or is no longer available';

    if (match) {
        const responseCodeMatch = errorMessage.match(/Response Code:\s*([^ ]+)/);
        const responseCode = responseCodeMatch ? responseCodeMatch[1] : "Unknown";

        return {
            reasonCode: match[1],
            reasonText: match[2].trim(),
            responseCode: responseCode,
        };
    }

    if (errorMessage.includes(alreadyRentedMessage.trim())) {
        return {
            reasonCode: "10001",
            reasonText: 'unitNotAvailable',
            responseCode: "Unknown",
        };
    }

    if (errorMessage.includes('3D Secure authentication failed')) {
        return {
            reasonCode: "50716",
            reasonText: '3DSecureAuthenticationFailed',
            responseCode: "Unknown",
        };
    }

    return null;
}

/**
 * Used for displaying the insurance premium INCLUDING tax as it doesn't include it by default
 * 
 * @param coverages 
 * @param selectedCoverage 
 * @param taxRate 
 * 
 * @returns 
 */
export const computedInsurancePremium = (coverages: InsuranceCoverage[], selectedCoverage: number, taxRate: number): number => {
    if (selectedCoverage === -1) {
        return 0;
    }

    const taxAmount = numberRound(coverages[selectedCoverage].dcPremium * (taxRate / 100));
    const premium = numberRound(coverages[selectedCoverage].dcPremium);
    const total = premium + taxAmount;

    return total;
}